/**
 * THE canonical implementation of booking pricing for the web app (#142) — the 1:1 TypeScript mirror of
 * the backend calculator `Shared.Core.Calculation.BookingCalculation`.
 *
 * Function names, parameter order and formula bodies are kept in lockstep with the C# original; the
 * parity is locked by the shared JSON fixtures in `fixtures/booking-calculation.fixtures.json`, which are
 * generated/verified by the backend test suite (`CheqMe.Booking.Calculation.Tests`,
 * `SharedFixtureTests`) and replayed against this library by `calculation-fixture-parity.spec.ts`.
 * If you change a formula here, the backend must change too — and vice versa.
 *
 * Vocabulary: the *single price* of a booked line is its base/override price plus all
 * interchanged-component (swap) price changes. The *distributed unit price* additionally spreads the
 * order-level business-case total proportionally over the order's lines — the canonical
 * revenue-per-line formula used by ALL analytics.
 */
export class BookingCalculation {
  /**
   * The single (per-unit) price of a booked composition line:
   * `(overridePrice ?? basePrice) + Σ priceChanges` (null price changes count as 0).
   */
  static singlePrice(basePrice: number, overridePrice: number | null | undefined,
                     priceChanges?: (number | null | undefined)[] | null): number {
    return (priceChanges?.reduce((sum: number, change) => sum + (change ?? 0), 0) ?? 0)
      + (overridePrice ?? basePrice);
  }

  /** The total price of a line: `quantity × singlePrice` (negative for storno lines). */
  static totalPrice(quantity: number, singlePrice: number): number {
    return quantity * singlePrice;
  }

  /** The sign of a business case: its quantity only encodes direction (+ booked / − storno). */
  static signIndicator(quantity: number): number {
    return quantity > 0 ? 1 : -1;
  }

  /**
   * Defensive resolution of a transmitted business case's sign (#F9). The server now serializes
   * `signIndicator` (it used to be `[JsonIgnore]`), but a partial/legacy payload could still omit it. A
   * missing value must never leak into the arithmetic as `undefined`, because `amount * undefined` = `NaN`,
   * and the Angular currency pipe renders `NaN` as an EMPTY string — a single poisoned business case would
   * blank the whole order/page total. When `signIndicator` is absent we derive it from `quantity` if that is
   * present, otherwise the case simply contributes nothing (0) rather than poisoning the sum.
   */
  static resolveSignIndicator(bc: { signIndicator?: number | null; quantity?: number | null }): number {
    if (typeof bc.signIndicator === "number" && !Number.isNaN(bc.signIndicator)) return bc.signIndicator;
    if (typeof bc.quantity === "number") return BookingCalculation.signIndicator(bc.quantity);
    return 0;
  }

  /**
   * The order-level business-case total: `Σ amount × signIndicator(quantity)`. Callers must pre-filter
   * to order-level cases (`!isFromOrder`).
   */
  static businessCaseTotal(businessCases: { amount: number; quantity: number }[]): number {
    return businessCases.reduce((sum, bc) => sum + bc.amount * BookingCalculation.signIndicator(bc.quantity), 0);
  }

  /**
   * DTO-shaped adapter of {@link businessCaseTotal}: transmitted business cases carry the
   * server-computed `signIndicator` (= `signIndicator(quantity)`) instead of the raw quantity.
   */
  static businessCaseTotalSigned(
    businessCases: { amount: number; signIndicator?: number | null; quantity?: number | null }[]): number {
    return businessCases.reduce((sum, bc) => sum + bc.amount * BookingCalculation.resolveSignIndicator(bc), 0);
  }

  /** The order's composition total: `Σ line totals` (before order-level business cases). */
  static orderCompositionTotal(lines: { quantity: number; singlePrice: number }[]): number {
    return lines.reduce((sum, line) => sum + BookingCalculation.totalPrice(line.quantity, line.singlePrice), 0);
  }

  /**
   * THE canonical distributed revenue formula (user decision #2 in #142): the order-level business-case
   * total is distributed proportionally across the order's lines by their share of the order total, then
   * normalized back to a per-unit price. A zero order total short-circuits the share to 0.
   */
  static distributedUnitPrice(lineTotal: number, orderTotal: number, businessCaseTotal: number,
                              quantity: number): number {
    return (lineTotal + (orderTotal === 0 ? 0 : lineTotal / orderTotal) * businessCaseTotal) / quantity;
  }

  /**
   * The order's grand total: composition total plus the signed order-level business-case total —
   * identical to summing all distributed line totals.
   */
  static orderGrandTotal(compositionTotal: number, businessCaseTotal: number): number {
    return compositionTotal + businessCaseTotal;
  }
}
